import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Coordinates mobile-only chrome elements (the bottom MobileTabBar,
 * floating HelpButton) with overlays that need the bottom of the
 * viewport — sticky action bars on long forms, fullscreen scanners,
 * etc. A ref-counted hook lets multiple consumers ask for the chrome
 * to hide simultaneously without stomping each other.
 */
interface MobileChromeContextValue {
  tabBarHiddenCount: number;
  pushHideTabBar: () => void;
  popHideTabBar: () => void;
}

const MobileChromeContext = createContext<MobileChromeContextValue | null>(null);

export const MobileChromeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const pushHideTabBar = useCallback(() => setCount((c) => c + 1), []);
  const popHideTabBar = useCallback(
    () => setCount((c) => Math.max(0, c - 1)),
    [],
  );
  return (
    <MobileChromeContext.Provider
      value={{ tabBarHiddenCount: count, pushHideTabBar, popHideTabBar }}
    >
      {children}
    </MobileChromeContext.Provider>
  );
};

export const useMobileChrome = (): MobileChromeContextValue => {
  const ctx = useContext(MobileChromeContext);
  if (!ctx) {
    throw new Error(
      'useMobileChrome must be used within a MobileChromeProvider',
    );
  }
  return ctx;
};

/**
 * Hook for components that should temporarily hide the bottom mobile
 * chrome while mounted (e.g. a StickyActionBar at the bottom of a
 * long form). Auto-pops on unmount.
 */
export const useHideMobileTabBar = (active: boolean = true): void => {
  const { pushHideTabBar, popHideTabBar } = useMobileChrome();
  React.useEffect(() => {
    if (!active) return;
    pushHideTabBar();
    return () => popHideTabBar();
  }, [active, pushHideTabBar, popHideTabBar]);
};
